import React from 'react'
import { Alert, AlertIcon } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { dates } from '@homevest/utils'
import axios from 'lib/axios'

type BuildiumBooksLockResponse = {
  lockDate: string | null
  today: string
}

export const isDatePostable = dates.isDatePostable

const isValidDateString = (date?: string | null) => {
  if (!date) {
    return false
  }

  try {
    dates.normalizeToDateString(date)
    return true
  } catch (_error) {
    return false
  }
}

export const useBuildiumBooksLockDate = (rentalId?: string, enabled = true) =>
  useQuery<BuildiumBooksLockResponse, Error>({
    queryKey: ['buildium-books-lock-date', rentalId],
    enabled: enabled && Boolean(rentalId),
    queryFn: async () => {
      const response = await axios.get('/admin/buildium/accounting-lock-date', {
        params: {
          rentalId
        }
      })

      return response.data
    },
    staleTime: 5 * 60 * 1000
  })

export const useBuildiumPostingGuard = ({
  date,
  enabled = true,
  rentalId
}: {
  date?: string | null
  enabled?: boolean
  rentalId?: string
}) => {
  const query = useBuildiumBooksLockDate(rentalId, enabled)
  const hasValidPostingDate = isValidDateString(date)
  const isBuildiumBooksLocked =
    hasValidPostingDate && !isDatePostable(date as string, query.data?.lockDate)

  return {
    booksLock: query.data,
    isBooksLockError: query.isError,
    isBooksLockLoading: query.isLoading,
    hasValidPostingDate,
    isBuildiumBooksLocked,
    retryBooksLock: query.refetch
  }
}

export const BooksLockedNotice: React.FC<
  React.PropsWithChildren<{
    lockDate?: string | null
    isError?: boolean
  }>
> = ({ lockDate, isError }) => (
  <Alert status='error' my={2}>
    <AlertIcon />
    {isError
      ? 'Buildium books-lock status could not be verified. Contact FinOps to confirm the Buildium books-locked date.'
      : `Buildium's books are locked as of ${lockDate}. Contact FinOps to unlock the books if you need to post something before this date.`}
  </Alert>
)
